import { SyntheticEvent, useRef } from 'react';
import './App.css';
import { ProgressBar } from 'primereact/progressbar';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Toast } from 'primereact/toast';
import { Navigate, useLocation, useNavigate } from 'react-router';
import BroadSearchAutoComplete from './components/broadSearch/BroadSearchAutoComplete';
import DivButtonOG from './components/utils/DivButton';
import { MenuEvent } from './shared/types/appTypes';
import AppRouter from './AppRouter';
import { useAuth } from './hooks/useAuth';
import { AuthContext } from './context/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { cantViewScope, getActiveScopes } from './services/ScopeService';
export default function App() {
  const toast = useRef<Toast>(null);
  const profileMenu = useRef<Menu>(null);
  const adminMenu = useRef<Menu>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const auth = useAuth(toast);
  const {
    isAuthenticated,
    user,
    profile,
    impersonation,
    isLoaded,
    factors: _factors,
    clearState,
    adminCheck,
    handleAuthentication,
    endImpersonation,
    homePage,
  } = auth;

  const goto = (e: MenuEvent, url: string) => {
    if (adminCheck(url.startsWith('admin'), profile, impersonation)) return;
    if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
      window.open(url, '_blank');
    } else {
      void navigate(url);
    }
  };

  const navTo = (e: React.MouseEvent | React.KeyboardEvent, url: string) => {
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
    } else {
      void navigate(url);
    }
  };

  // Redirect unauthenticated root visits to /login
  if (!user && location.pathname === '/') {
    return <Navigate to='/login' replace />;
  }
  // Clear stale state when a logged-in user arrives at / with search params
  // (e.g. after a forced logout redirect that preserved query params).
  if (user && location.pathname === '/' && location.search !== '') {
    clearState();
    return <Navigate to='/login' replace />;
  }
  // Logged-in root visits are handled by AppRouter's / → homePage route.
  const routing = (
    <ErrorBoundary>
      <AuthContext.Provider value={auth}>
        <AppRouter
          handleAuthentication={handleAuthentication}
          homePage={homePage}
          user={user}
        />
      </AuthContext.Provider>
    </ErrorBoundary>
  );

  const isAIAdmin = isAuthenticated && profile?.user_type === 'augintel_admin';
  const isAgencyAdmin =
    isAuthenticated && profile?.user_type === 'agency_admin';
  const isImpersonatedAdmin =
    isAuthenticated && impersonation?.user_type === 'agency_admin';

  const queriesNavigation = (
    <DivButtonOG
      className='nav-header-button aLink navigation p-sm-hide p-md-hide queries'
      onClick={(e) => navTo(e, '/queries')}
    >
      <span className='icon'>
        <svg width='16' height='16' viewBox='0 0 16 16'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M14.2222 0H1.77778C0.8 0 0 0.8 0 1.77778V14.2222C0 15.2 0.8 16 1.77778 16H14.2222C15.2 16 16 15.2 16 14.2222V1.77778C16 0.8 15.2 0 14.2222 0ZM14.2222 6.4L8.88889 12.4444L5.33333 8.88889L1.77778 12.4444V9.77778L5.33333 6.22222L8.88889 9.77778L14.2222 3.73333V6.4Z'
          />
        </svg>
      </span>
      <span className='text'>Queries</span>
    </DivButtonOG>
  );

  const adminNavigation =
    (impersonation && isImpersonatedAdmin) ||
    (!impersonation && (isAIAdmin || isAgencyAdmin)) ? (
      <DivButtonOG
        className='nav-header-button aLink navigation p-sm-hide p-md-hide p-lg-hide admin'
        onClick={(e: SyntheticEvent) => adminMenu.current?.toggle(e)}
      >
        <span className='icon'>
          <svg width='16' height='16' viewBox='0 0 512 512'>
            <path
              fill='currentColor'
              d='M496 384H160v-16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v16H16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h80v16c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-16h336c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zm0-160h-80v-16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v16H16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h336v16c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-16h80c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zm0-160H288V48c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v16H16C7.2 64 0 71.2 0 80v32c0 8.8 7.2 16 16 16h208v16c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-16h208c8.8 0 16-7.2 16-16V80c0-8.8-7.2-16-16-16z'
              className=''
            />
          </svg>
        </span>
        <span className='text'>Admin</span>
      </DivButtonOG>
    ) : (
      ''
    );

  const adminMenuItems = [
    {
      label: <div className='aLink'>Users</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/users');
      },
      className: isAIAdmin ? '' : 'hide',
    },
    {
      label: <div className='aLink'>Agencies</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/agencies');
      },
      className: isAIAdmin ? '' : 'hide',
    },
    {
      label: <div className='aLink'>Agency Groups</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/groups');
      },
      className: isAIAdmin ? '' : 'hide',
    },
    {
      label: <div className='aLink'>Query Groups</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/queryGroups');
      },
      className: isAIAdmin ? '' : 'hide',
    },
    {
      label: <div className='aLink'>Queries</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/queries');
      },
      className: isAIAdmin ? '' : 'hide',
    },
    {
      label: <div className='aLink'>Assignments</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/assignments');
      },
      className: isAIAdmin ? '' : 'hide',
    },
    {
      label: <div className='aLink'>Usage Reporting</div>,
      command: (e: MenuEvent) => {
        goto(e, '/admin/usageReporting');
      },
    },
  ];
  const adminPopup =
    isAIAdmin || isAgencyAdmin ? (
      <Menu
        className='adminMenu'
        popup
        ref={adminMenu}
        model={adminMenuItems as unknown as MenuItem[]}
        appendTo='self'
      />
    ) : (
      ''
    );

  // State is cleared by clearState() in useAuth.ts on logout:
  // localStorage, React state, Datadog session replay, Mixpanel session recording.
  const navigation = isAuthenticated ? (
    <div className='mid p-sm-hide p-md-hide'>
      <DivButtonOG
        className='nav-header-button aLink navigation p-sm-hide p-md-hide cases'
        onClick={(e) => navTo(e, '/cases')}
      >
        <span className='icon'>
          <svg width='16' height='16' viewBox='0 0 512 512'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M320 336c0 8.84-7.16 16-16 16h-96c-8.84 0-16-7.16-16-16v-48H0v144c0 25.6 22.4 48 48 48h416c25.6 0 48-22.4 48-48V288H320v48zm144-208h-80V80c0-25.6-22.4-48-48-48H176c-25.6 0-48 22.4-48 48v48H48c-25.6 0-48 22.4-48 48v80h512v-80c0-25.6-22.4-48-48-48zm-144 0H192V96h128v32z'
            />
          </svg>
        </span>
        <span className='text'>Cases</span>
      </DivButtonOG>
      {getActiveScopes(profile, impersonation).includes(
        'monitors_enabled_in_elevate',
      ) && (
        <DivButtonOG
          className='nav-header-button aLink navigation p-sm-hide p-md-hide monitors'
          onClick={(e) => navTo(e, '/monitors')}
        >
          <span className='icon'>
            <svg width='16' height='16' viewBox='0 0 448 512'>
              <path d='M224 0c-17.7 0-32 14.3-32 32v19.2C119 66 64 130.6 64 208v18.8c0 47-17.3 92.4-48.5 127.6l-7.4 8.3C-2.4 375.3 7.4 396 22.8 396H425.2c15.4 0 25.2-20.7 14.7-33.3l-7.4-8.3C401.3 319.2 384 273.8 384 226.8V208c0-77.4-55-142-128-156.8V32c0-17.7-14.3-32-32-32zm45.3 493.3c12-12 18.7-28.3 18.7-45.3H160c0 17 6.7 33.3 18.7 45.3S207 512 224 512s33.3-6.7 45.3-18.7z' />
            </svg>
          </span>
          <span className='text'>Monitors</span>
        </DivButtonOG>
      )}
      {!cantViewScope('queries_disabled_in_elevate', profile, impersonation) &&
        queriesNavigation}
      {adminPopup}
      {adminNavigation}
      <DivButtonOG
        className='nav-header-button aLink navigation p-sm-hide p-md-hide p-lg-hide help'
        onClick={(e) => navTo(e, '/contact')}
      >
        <span className='text'>Contact Us</span>
      </DivButtonOG>
    </div>
  ) : (
    <div className='mid'>
      <DivButtonOG
        className='nav-header-button aLink navigation'
        onClick={(e) => navTo(e, '/')}
      >
        Augintel
      </DivButtonOG>
      <DivButtonOG
        className='nav-header-button aLink navigation p-sm-hide p-md-hide p-lg-hide help'
        onClick={(e) => navTo(e, '/contact')}
      >
        <span className='text'>Contact Us</span>
      </DivButtonOG>
    </div>
  );

  const search = isAuthenticated ? (
    <BroadSearchAutoComplete
      toast={toast}
      profile={profile}
      impersonation={impersonation}
    />
  ) : (
    ''
  );

  const profileMenuItems = [
    {
      label: <p>{impersonation?.email || user?.email}</p>,
      className: 'user-auth',
    },
    {
      label: <div className='aLink profileLink cases'>Cases</div>,
      command: (e: MenuEvent) => {
        goto(e, '/cases');
      },
    },
    {
      label: <div className='aLink profileLink queries'>Queries</div>,
      command: (e: MenuEvent) => {
        goto(e, '/queries');
      },
      className: '',
    },
    {
      label: (
        <div className='aLink profileLink endImpersonation'>
          End Impersonation
        </div>
      ),
      command: () => {
        endImpersonation();
      },
      className: impersonation ? '' : 'hide',
    },
    {
      label: <div className='aLink profileLink help'>Contact Us</div>,
      command: (e: MenuEvent) => {
        goto(e, '/contact');
      },
    },
    {
      label: (
        <div className='aLink profileLink emailSettings'>Email Settings</div>
      ),
      command: (e: MenuEvent) => {
        goto(e, '/emailsettings');
      },
      className: 'start',
    },
    {
      label: (
        <div className='aLink profileLink changePassword'>Change Password</div>
      ),
      command: (e: MenuEvent) => {
        goto(e, '/changepassword');
      },
      className: 'start',
    },
    {
      label: <div className='aLink profileLink logout'>Logout</div>,
      command: (e: MenuEvent) => {
        goto(e, '/login');
      },
      className: 'start',
    },
  ];

  const profilePopup = isAuthenticated ? (
    <Menu
      className='profileMenu'
      popup
      ref={profileMenu}
      model={profileMenuItems as unknown as MenuItem[]}
      appendTo='self'
    />
  ) : (
    ''
  );

  const profileIcon = isAuthenticated ? (
    <div className='right'>
      <DivButtonOG
        className='user-menu-toggle profile p-sm-hide p-md-hide aLink'
        onClick={(e: SyntheticEvent) => profileMenu.current?.toggle(e)}
      >
        {impersonation
          ? impersonation.email.charAt(0).toUpperCase()
          : user?.email.charAt(0).toUpperCase()}
      </DivButtonOG>
      <DivButtonOG
        className='hamburger-toggle menu p-lg-hide p-xl-hide'
        onClick={(e: SyntheticEvent) => profileMenu.current?.toggle(e)}
      >
        <svg width='36' height='36' viewBox='0 0 24 24'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z'
          />
        </svg>
      </DivButtonOG>
      {profilePopup}
    </div>
  ) : (
    ''
  );

  const header = (
    <div className='appHeader'>
      <div className='left p-sm-hide'>
        <DivButtonOG
          className='aLink'
          onClick={() => {
            void navigate('/');
          }}
        >
          <img
            className='logo'
            src='/augintel_logomark_blue.svg'
            alt='Augintel logo mark (blue)'
          />
        </DivButtonOG>
      </div>
      {navigation}
      {search}
      {profileIcon}
    </div>
  );

  const isAdmin = profile?.user_type === 'augintel_admin';
  const classNames = [
    'app',
    isAdmin ? 'admin' : '',
    impersonation ? 'impersonation' : '',
  ].join(' ');

  // TODO: update import, optimize loads
  return !isLoaded ? (
    <ProgressBar mode='indeterminate' />
  ) : (
    <div className={classNames}>
      <link rel='preconnect' href={import.meta.env.VITE_API_URL} />
      <link rel='dns-prefetch' href={import.meta.env.VITE_API_URL} />
      {header}
      <Toast ref={toast} />
      {routing}
    </div>
  );
}
